import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, Phone, Mail, Clock, ChevronDown } from "lucide-react";
import { SITE, waLink } from "../config/site";
import { KineticText } from "../components/KineticText";
import { ParticleField } from "../components/ParticleField";
import { Reveal } from "../components/Reveal";
import { InquiryForm } from "../components/InquiryForm";
import { WhatsAppIcon } from "../components/WhatsAppIcon";

const FAQS = [
  { q: "How much does a project cost?", a: "Landing pages start around ₹40,000; full web or mobile apps typically range from ₹1.5L to ₹8L+ depending on scope. After a short discovery call we share a fixed, itemised quote — no surprises later." },
  { q: "How long does a typical build take?", a: "A premium marketing website ships in 3–4 weeks. Web apps and mobile apps usually run 8–14 weeks with weekly staging demos from the very first sprint." },
  { q: "Do you work with clients outside Mumbai?", a: "Absolutely. Roughly half our clients are outside Maharashtra — across India, the Middle East, Europe and North America. We overlap time zones for calls and work async everywhere else." },
  { q: "Will I own the code and design files?", a: "Yes — 100%. Repositories, Figma files, cloud accounts and documentation are all in your name from day one. No lock-in, ever." },
  { q: "Do you sign NDAs?", a: "Happily. We're glad to sign your NDA before any detailed discussion, or share our standard mutual NDA template." },
];

const INFO = [
  { icon: MapPin, label: "Visit Us", value: SITE.address, testid: "contact-address" },
  { icon: Phone, label: "Call Us", value: SITE.phoneDisplay, href: SITE.phoneHref, testid: "contact-phone" },
  { icon: Mail, label: "Email Us", value: SITE.email, href: `mailto:${SITE.email}`, testid: "contact-email" },
  { icon: Clock, label: "Working Hours", value: SITE.hours, testid: "contact-hours" },
];

export default function Contact() {
  const [params] = useSearchParams();
  const serviceParam = params.get("service") || "";
  const [openFaq, setOpenFaq] = useState(0);

  return (
    <main data-testid="contact-page">
      {/* HERO */}
      <section className="grain relative overflow-hidden bg-night pb-16 pt-36 lg:pb-24 lg:pt-44">
        <ParticleField className="absolute inset-0 h-full w-full" />
        <div className="pointer-events-none absolute -left-32 top-0 h-80 w-80 rounded-full bg-spark/15 blur-[110px]" />
        <div className="relative mx-auto max-w-7xl px-4 text-center sm:px-6 lg:px-8 lg:text-left">
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="font-mono text-xs uppercase tracking-[0.3em] text-spark"
          >
            Contact Us · Response within 24 hours
          </motion.p>
          <h1 className="mt-6 font-syne text-4xl font-extrabold leading-[1.02] tracking-tight text-white sm:text-6xl lg:text-7xl" data-testid="contact-headline">
            <KineticText
              lines={[
                "LET'S START",
                <span key="s" className="bg-gradient-to-r from-spark to-ember bg-clip-text text-transparent">SOMETHING</span>,
                "GREAT.",
              ]}
              delay={0.25}
            />
          </h1>
        </div>
      </section>

      {/* FORM + INFO */}
      <section className="bg-silk py-20 lg:py-28">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 lg:grid-cols-5 lg:gap-14 lg:px-8">
          <Reveal className="lg:col-span-3">
            <div className="rounded-[2rem] border border-ink/10 bg-white p-7 shadow-xl shadow-ink/5 sm:p-10">
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-ember">Inquiry Form</p>
              <h2 className="mt-3 font-syne text-2xl font-extrabold text-ink sm:text-3xl">
                Tell us about your project
              </h2>
              <p className="mt-2 text-sm text-slate-500">
                Fill this in and we'll open WhatsApp with everything pre-written — one tap and your inquiry is with our team.
              </p>
              <div className="mt-8">
                <InquiryForm key={serviceParam} initialService={serviceParam || undefined} />
              </div>
            </div>
          </Reveal>

          <div className="space-y-5 lg:col-span-2">
            {INFO.map((item, i) => (
              <Reveal key={item.label} delay={i * 0.06}>
                <div className="rounded-3xl border border-ink/10 bg-white p-6" data-testid={item.testid}>
                  <div className="flex items-center gap-3">
                    <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-night text-spark">
                      <item.icon className="h-4.5 w-4.5 h-5 w-5" />
                    </span>
                    <p className="font-mono text-xs uppercase tracking-[0.25em] text-slate-500">{item.label}</p>
                  </div>
                  {item.href ? (
                    <a href={item.href} className="mt-3 block text-sm font-semibold leading-relaxed text-ink transition-colors hover:text-spark">
                      {item.value}
                    </a>
                  ) : (
                    <p className="mt-3 text-sm font-semibold leading-relaxed text-ink">{item.value}</p>
                  )}
                </div>
              </Reveal>
            ))}
            <Reveal delay={0.25}>
              <a
                href={waLink("Hello SparkGrid Solutions! I'd like to discuss a project.")}
                target="_blank"
                rel="noreferrer"
                data-testid="contact-whatsapp-btn"
                className="flex items-center justify-center gap-2 rounded-3xl bg-wa px-6 py-5 text-sm font-bold text-white shadow-lg shadow-wa/30 transition-transform duration-300 hover:scale-[1.02]"
              >
                <WhatsAppIcon className="h-5 w-5" /> Chat Instantly on WhatsApp
              </a>
            </Reveal>
          </div>
        </div>

        {/* MAP */}
        <div className="mx-auto mt-14 max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="overflow-hidden rounded-[2rem] border border-ink/10 shadow-xl shadow-ink/5" data-testid="contact-map">
              <iframe
                title="SparkGrid Solutions — Mira Road East, Mumbai"
                src={`https://maps.google.com/maps?q=${encodeURIComponent(SITE.mapQuery)}&z=14&output=embed`}
                className="h-80 w-full border-0"
                loading="lazy"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-ink/10 bg-white py-20 lg:py-28">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="text-center font-mono text-xs uppercase tracking-[0.3em] text-ember">FAQ</p>
            <h2 className="mt-3 text-center font-syne text-3xl font-extrabold tracking-tight text-ink sm:text-4xl">
              Questions, answered.
            </h2>
          </Reveal>
          <div className="mt-12 space-y-4">
            {FAQS.map((f, i) => (
              <Reveal key={f.q} delay={i * 0.05}>
                <div className="overflow-hidden rounded-2xl border border-ink/10 bg-silk" data-testid={`faq-item-${i}`}>
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                    data-testid={`faq-toggle-${i}`}
                    className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left"
                  >
                    <span className="font-syne text-base font-bold text-ink sm:text-lg">{f.q}</span>
                    <ChevronDown className={`h-5 w-5 shrink-0 text-ember transition-transform duration-300 ${openFaq === i ? "rotate-180" : ""}`} />
                  </button>
                  <AnimatePresence initial={false}>
                    {openFaq === i && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                      >
                        <p className="px-6 pb-6 text-sm leading-relaxed text-slate-600">{f.a}</p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
